import { buildRequestHeadersWithAuthToken, getAppStore } from '../../../../../@data';
import { uiConfig } from '../../../../../config/ui.config';
import { endOfDay, startOfDay } from '../../../../../utils';
import { TythonColumnsEnum } from '../../../@data/store/schema/ColumnsEnum';
import { getPublisherId } from '../../../@data/store/store';
import { ChartGrain } from '../../../DashboardCards/ReportCard/ReportCard.types';
import { RangeType } from '../../CustomCalender/CustomCalender.types';
import { ISavedReportItem } from '../LeftHandNavigation.types';

type ReportType = {
  reportName: string;
  filters: {};
  columns: TythonColumnsEnum[];
  granularity: ChartGrain;
  reportType: string;
  dateTimeRanges: { startDateTime: string; endDateTime: string }[];
  adDistribution: string;
};

type ReportDeliveryType = {
  emailIds: string[];
  compressReport: true;
  sendAsAttachment: true;
};

type ReportScheduleType = {
  isScheduleActive: boolean;
  scheduleStartTime?: string;
  nextRunTime?: string | null;
  lastRunTime?: string | null;
  customSchedule?: string | null;
  defaultSchedule?: {
    scheduleType: string;
    intervalValue: number;
  };
};
interface ISavedReportsResponse {
  data: {
    savedReports: Array<{
      id: number;
      name: string;
      isScheduled: boolean;
      isDefault: boolean;
      report: ReportType;
      reportDelivery?: ReportDeliveryType;
      reportSchedule?: ReportScheduleType;
      duration: string;
    }>;
    totalRecords: number;
  };
  trackingId: string;
}

interface IUpdateSavedReportResponse {
  data: {
    updatedSavedReports: Array<{
      id: number;
      name: string;
      isScheduled: boolean;
      isDefault: boolean;
      report: ReportType;
      reportDelivery?: ReportDeliveryType;
      reportSchedule?: ReportScheduleType;
      duration: string;
    }>;
    totalRecords: number;
  };
  trackingId: string;
}

interface ISavedReportsRequest {
  savedReports: Array<{
    id?: number | null;
    name: string;
    isScheduled: boolean;
    isDefault: boolean;
    report: ReportType;
    reportDelivery: ReportDeliveryType;
    reportSchedule: ReportScheduleType;
    Duration: number | null;
    version: number;
  }>;
}

const RangeTypeMap: Record<RangeType, number | null> = {
  [RangeType.Today]: 1,
  [RangeType.Yesterday]: 2,
  [RangeType.Last7ExcludingToday]: 3,
  [RangeType.ThisMonth]: 4,
  [RangeType.LastMonth]: 5,
  [RangeType.Last30Days]: 6,
  [RangeType.SundayToday]: 7,
  [RangeType.MondayToday]: 8,
  [RangeType.Last7FromSunToSat]: 9,
  [RangeType.Custom]: null,
};

const DurationToRangeType: Record<string, RangeType> = {
  Today: RangeType.Today,
  Yesterday: RangeType.Yesterday,
  Last7Days: RangeType.Last7ExcludingToday,
  ThisMonth: RangeType.ThisMonth,
  LastMonth: RangeType.LastMonth,
  Last30Days: RangeType.Last30Days,
  ThisWeekSunday: RangeType.SundayToday,
  ThisWeekMonday: RangeType.MondayToday,
  LastWeekSunday: RangeType.Last7FromSunToSat,
};

const mapAPIGrainToCodeGrain = (grain?: string): ChartGrain => {
  switch (grain) {
    case 'Hourly':
      return ChartGrain.HOURLY;
    case 'Daily':
      return ChartGrain.DAILY;
    case 'Weekly':
      return ChartGrain.WEEKLY;
    case 'Monthly':
      return ChartGrain.MONTHLY;
    default:
      return ChartGrain.MONTHLY;
  }
};

const mapGrainToApiValue = (grain?: ChartGrain): string => {
  switch (grain) {
    case ChartGrain.HOURLY:
      return 'Hourly';
    case ChartGrain.DAILY:
      return 'Daily';
    case ChartGrain.WEEKLY:
      return 'Weekly';
    case ChartGrain.MONTHLY:
      return 'Monthly';
    default:
      return 'Monthly';
  }
};

const constructRequestBody = (report: ISavedReportItem, requestType: 'PUT' | 'POST'): ISavedReportsRequest => {
  const dateTimeRanges =
    report.rangeType === RangeType.Custom
      ? [
          {
            startDateTime: startOfDay(report.dateRange.startDate).toISOString(),
            endDateTime: endOfDay(report.dateRange.endDate).toISOString(),
          },
        ]
      : [];
  return {
    savedReports: [
      {
        id: requestType === 'PUT' ? parseInt(report.id, 10) : null,
        name: report.name,
        isScheduled: report.isScheduled,
        isDefault: false,
        report: {
          reportName: report.name,
          filters: report.filters,
          columns: report.columns,
          granularity: report.grain,
          reportType: report.reportType,
          dateTimeRanges,
          adDistribution: report.filters['AdDistribution']?.[0] || 'PaidSearch',
        },
        reportDelivery: { emailIds: report.reportDeliveryEmails || [], compressReport: true, sendAsAttachment: true },
        reportSchedule: {
          isScheduleActive: report.isScheduled,
          scheduleStartTime: new Date().toISOString(),
          nextRunTime: null,
          lastRunTime: null,
          customSchedule: null,
          defaultSchedule: {
            scheduleType: mapGrainToApiValue(report.reportScheduleFrequency),
            intervalValue: 1,
          },
        },
        Duration: RangeTypeMap[report.rangeType],
        version: 2,
      },
    ],
  };
};

const mapResponseToSavedReportItem = (response: IUpdateSavedReportResponse['data']['updatedSavedReports']): ISavedReportItem[] => {
  return response.map((report) => ({
    id: report.id.toString(),
    name: report.name,
    isSaved: true,
    isDefault: report.isDefault,
    filters: report.report.filters,
    columns: report.report.columns,
    grain: report.report.granularity,
    reportType: report.report.reportType,
    dateRange: {
      startDate: new Date(report.report.dateTimeRanges[0].startDateTime),
      endDate: new Date(report.report.dateTimeRanges[0].endDateTime),
    },
    isScheduled: report.isScheduled && (report.reportSchedule?.isScheduleActive ?? false),
    reportScheduleFrequency: mapAPIGrainToCodeGrain(report.reportSchedule?.defaultSchedule?.scheduleType),
    reportDeliveryEmails: report.reportDelivery?.emailIds || [],
    rangeType: report.duration ? DurationToRangeType[report.duration] ?? RangeType.Custom : RangeType.Custom,
  }));
};
const userContext = getAppStore().userContext;
const getUrl = (version: string, suffix?: string) =>
  `${uiConfig.getApiBaseAddress(version)}/publishers/${getPublisherId()}/reports/savedreports${suffix ?? ''}`;

export async function getSavedReports(): Promise<ISavedReportItem[]> {
  try {
    const url = getUrl('1.3');
    const headers = await buildRequestHeadersWithAuthToken(url, userContext);
    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      console.error(`HTTP error! Status: ${response.status}`);
      return [];
    }

    const data: ISavedReportsResponse = await response.json();
    return mapResponseToSavedReportItem(data.data.savedReports);
  } catch (error) {
    console.error('Error fetching saved reports:', error);
    return [];
  }
}

export async function updateSavedReport(savedReport: ISavedReportItem): Promise<ISavedReportItem | undefined> {
  try {
    const url = getUrl('1.3');
    const headers = await buildRequestHeadersWithAuthToken(url, userContext);
    const body = constructRequestBody(savedReport, 'PUT');

    const response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) });
    if (!response.ok) {
      console.error(`HTTP error! Status: ${response.status}`);
      return undefined;
    }

    const data: IUpdateSavedReportResponse = await response.json();
    return mapResponseToSavedReportItem(data.data.updatedSavedReports)[0];
  } catch (error) {
    return undefined;
  }
}

export async function addSavedReport(savedReport: ISavedReportItem): Promise<boolean> {
  try {
    const url = getUrl('1.3');
    const headers = await buildRequestHeadersWithAuthToken(url, userContext);
    const body = constructRequestBody(savedReport, 'POST');

    const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });

    return response.ok;
  } catch (error) {
    console.error('Error updating saved report:', error);
    return false;
  }
}

export async function deleteSavedReport(reportId: string) {
  try {
    const url = getUrl('1.2', `/${reportId}`);
    const headers = await buildRequestHeadersWithAuthToken(url, userContext);
    const response = await fetch(url, { method: 'DELETE', headers });

    return response.ok;
  } catch (err) {
    return false;
  }
}
